/**
 * The cohort list, in words (25.09.2026).
 *
 * Sixteen cohorts in one flat table meant reading every row to find the ones
 * that matter today. What matters is nearly always time: what starts soon,
 * what is running, what is over. These are the rules for saying that, kept
 * pure so the table can be argued with in tests rather than on screen.
 */

const DAY = 86400000;
const MONTHS = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
  "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];
const MONTH_SHORT = ["ian.", "feb.", "mar.", "apr.", "mai", "iun.",
  "iul.", "aug.", "sept.", "oct.", "nov.", "dec."];

/** Cohort dates are plain `YYYY-MM-DD`; midday keeps every timezone on the
 *  intended day, as on the public site. */
function parse(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function midday(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
}

/** `2026-09-28` → `28 sept.` */
export function shortDay(iso) {
  const d = parse(iso);
  return d ? `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}` : "";
}

/** The period as one line: `28 sept. → 14 oct.` */
export function periodLabel(group) {
  const start = shortDay(group?.start_date);
  const end = shortDay(group?.end_date);
  if (!start) return "fără dată";
  return end && end !== start ? `${start} → ${end}` : start;
}

/**
 * Where this cohort is in its life: `urmeaza`, `in_curs` or `incheiata`.
 *
 * The end date decides when there is one; without it, a cohort that started
 * is treated as running rather than finished — the opposite guess would hide
 * it from the default view while it is still being taught.
 */
export function groupPhase(group, now = new Date()) {
  const today = midday(now);
  const start = parse(group?.start_date);
  const end = parse(group?.end_date);
  if (end && end < today) return "incheiata";
  if (start && start > today) return "urmeaza";
  if (!start) return "urmeaza";
  return "in_curs";
}

/** How far off it is, in words: what the eye is looking for when scanning. */
export function phaseNote(group, now = new Date()) {
  const today = midday(now);
  const start = parse(group?.start_date);
  const end = parse(group?.end_date);
  const phase = groupPhase(group, now);
  // A cohort starting today counts as running — it is today's — but what the
  // eye wants to read is that it starts today.
  if (start && Math.round((midday(start) - today) / DAY) === 0) return "începe azi";
  if (phase === "urmeaza") {
    if (!start) return "fără dată de start";
    const days = Math.round((midday(start) - today) / DAY);
    if (days === 1) return "începe mâine";
    if (days <= 14) return `începe în ${days} zile`;
    return `începe pe ${start.getDate()} ${MONTHS[start.getMonth()]}`;
  }
  if (phase === "in_curs") {
    const total = (group?.sessions || []).length;
    if (total) {
      const done = group.sessions.filter((s) => {
        const d = parse(s?.date);
        return d && d < today;
      }).length;
      return `în desfășurare · ${Math.min(done + 1, total)} din ${total} sesiuni`;
    }
    return end ? `în desfășurare, până pe ${shortDay(group.end_date)}` : "în desfășurare";
  }
  return end ? `încheiată pe ${shortDay(group.end_date)}` : "încheiată";
}

/**
 * The tabs above the list. `lucru` is the default because it is the only one
 * that answers "what do I have to do about a cohort today".
 */
export const GROUP_TABS = [
  { key: "lucru", label: "În lucru", phases: ["urmeaza", "in_curs"] },
  { key: "incheiate", label: "Încheiate", phases: ["incheiata"] },
  { key: "toate", label: "Toate", phases: null },
  { key: "arhivate", label: "Arhivate", archived: true },
];

/**
 * Does this cohort belong under that tab?
 *
 * The archive is asked for by the server, but the mark is checked here too:
 * a deploy puts the new page in front of people minutes before the API
 * catches up, and in that window an ignored `archived` parameter would fill
 * the archive tab with cohorts that are not archived at all (25.09.2026).
 */
export function matchesTab(group, tab, now = new Date()) {
  const rule = GROUP_TABS.find((t) => t.key === tab) || GROUP_TABS[0];
  const archived = Boolean(group?.archived);
  if (rule.archived) return archived;
  if (archived) return false;
  if (!rule.phases) return true;
  return rule.phases.includes(groupPhase(group, now));
}

/** Free text over the fields somebody would actually type: name, code, notes. */
export function matchesSearch(group, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return [group?.name, group?.description, group?.notes, group?.format]
    .some((v) => String(v || "").toLowerCase().includes(q));
}

/** How full, as a share of the seats — for the bar beside the numbers. */
export function seatShare(group) {
  const max = Number(group?.max_seats) || 0;
  if (!max) return 0;
  return Math.min(1, Math.max(0, Number(group?.enrolled_count || 0) / max));
}

/**
 * What the cohort still owes before it can start: said as short warnings,
 * because these are the two that cost us this week — a cohort missing from
 * Logiscool, and one that cannot send its reminders for want of a link.
 */
export function readyWarnings(group, now = new Date()) {
  const out = [];
  // Nothing is owed by a cohort that is over, or one nobody will teach again.
  if (group?.archived) return out;
  if (groupPhase(group, now) === "incheiata") return out;
  if (!group?.limo_group_id) {
    out.push(group?.limo_test_group_id ? "doar în proba Logiscool" : "necreată în Logiscool");
  }
  if (!String(group?.access_link || "").trim()) out.push("fără link de acces");
  return out;
}

const CLOSE_PRESETS = [
  { value: "", label: "1 oră (implicit)" },
  { value: "30", label: "30 de minute" },
  { value: "120", label: "2 ore" },
  { value: "360", label: "6 ore" },
  { value: "1440", label: "o zi" },
  { value: "2880", label: "două zile" },
  { value: "0", label: "chiar la ora de start" },
];

export { CLOSE_PRESETS };

/**
 * When this cohort stops taking enrolments, in words.
 *
 * The moment itself is computed on the server, from the first session and the
 * cohort's own margin, and travels as `enrolment_closes_at`.
 */
export function closingNote(group, now = new Date()) {
  const iso = group?.enrolment_closes_at;
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const when = at.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
  return at <= now
    ? `Înscrierile s-au închis pe ${when}; grupa nu mai apare pe site.`
    : `Înscrierile se închid pe ${when}, apoi grupa iese de pe site.`;
}

/** Has this cohort already left the public site? */
export function enrolmentClosed(group, now = new Date()) {
  if (group?.enrolment_open === false) return true;
  const iso = group?.enrolment_closes_at;
  if (!iso) return false;
  const at = new Date(iso);
  return !Number.isNaN(at.getTime()) && at <= now;
}
